import type { ICallInfo } from "@/chats/messenger/messenger-models";
import type { IMediaType } from "@/models";
import type {
  ESocialPlatform,
  ESocialPlatformPopularity,
} from "./apply-to-be-featured-popup/enums";
import type { EPayoutMethod } from "./earnings/earnings-models";

export interface IOnboardingInitConfig {
  email?: string;
  code_used?: string;
  click_id?: string;
  source_flow?: string;
  isVerifyResend?: boolean;
  isAlreadyConverted?: boolean;
  isCodeOnboarding?: boolean;
  isClosable?: boolean;
}

interface IOnboardingCompleteConfig {
  token?: string;
  isJustSetUserName?: boolean;
}

export interface IOnboardingConfig {
  init?: IOnboardingInitConfig;
  complete?: IOnboardingCompleteConfig;
}

// Lives here, not account/onboarding/index.tsx, to avoid an import cycle.
export enum EOnboardingType {
  Init = "init",
  Complete = "complete",
}

export interface IEmailSettings {
  marketing: boolean;
  notifications: boolean;
  payments: boolean;
  payouts: boolean;
  commissions: boolean;
  subscriptions?: boolean;
}

interface IVideoRate {
  start: number;
  end: number;
  rate: number;
}

export enum EUserType {
  User = "user",
  Featured = "featured",
}

export enum EDiscoverStatus {
  Featured = "featured",
  Hidden = "hidden",
  Denied = "denied",
}

export enum EIdVerificationStatus {
  Pending = "pending",
  InProgress = "in_progress",
  Rejected = "rejected",
  Verified = "verified",
}

export enum ESocialPlatformStatus {
  InProgress = "in_progress",
  Rejected = "rejected",
  Verified = "verified",
}

export enum EIdVerificationMediaStatus {
  Verified = "verified",
  Retake = "retake",
}

interface IUserEssentialInfo {
  _id: string;
  display_name: string;
  username: string;
}

interface IProfileCompletionConfig {
  is_affiliate_profile_visited?: boolean;
  is_avatar?: boolean;
  is_bought_credit_package?: boolean;
  is_messaged_five_members?: boolean;
  is_auto_messages_visited?: boolean;
  is_auto_messages_enabled?: boolean;
  // is_broadcast_visited?: boolean;
}

export enum EGenderType {
  Male = "male",
  Female = "female",
  Trans = "trans",
}

export interface IFeedRemovedWarning {
  title: string;
  message: string;
  rules_url: string;
  removed_at: string; // ISO 8601
}

export interface IUserInfo extends IUserEssentialInfo {
  // The call this user is on or being rung for right now, so a launch, refresh
  // or resume can pick it up; the live events that carry it are missed while the
  // app is closed (Trello #8362). Taken into the call store and cleared again.
  active_call?: ICallInfo | null;
  auto_messages_available?: boolean;
  auto_messages_on?: boolean;
  affiliate?: string; // model._id or undefined for tiger1's
  profile_complete_config: IProfileCompletionConfig;
  latitude: number;
  longitude: number;
  app_version: string;
  avatar: IMediaType;
  allow_incoming_calls?: boolean; // global video-call availability, defaults to true
  bio: string;
  blocked_countries_count?: number;
  blocked_users_count?: number;
  is_boosted: boolean;
  boosted_till: string;
  block_chat: boolean;
  can_intro: boolean;
  can_sendphoto: boolean;
  can_set_rate: boolean; // default chat rate
  can_change_chat_rate: boolean; // direct chat rate
  cc_added: boolean;
  chars_per_msg: number;
  chat_id?: string; // only for direct request (e.g. won't be in discover user object) and it is the id of the chat with this user (if any)
  credit_rate: number;
  call_rate?: number; // credits per minute for video calls
  city: string;
  code_confirmed: boolean;
  code_used: string;
  code_used_is_featured: boolean;
  is_super_agent?: boolean;
  country: string; // use alpha2 code instead (country_code)
  country_code: string; // alpha2
  created_at: string;
  credits: number;
  unread_feeds?: number;
  deactivated: boolean;
  debug?: string;
  discover?: IMediaType;
  discover_status?: EDiscoverStatus; // fm only
  email: string;
  email_bounced: boolean;
  email_settings: IEmailSettings;
  email_verified?: boolean;
  facebook_connected?: boolean;
  featured: IMediaType[];
  featured_enquiry: boolean;
  feed_removed_warning?: IFeedRemovedWarning | null; // only in own profile, null when nothing to show
  force_discover?: boolean; // coupled with discover_status === EDiscoverStatus.Hidden will force to show discover
  force_free_chats?: boolean;
  force_free_chats_subs?: boolean;
  gender: EGenderType;
  greeting?: IMediaType;
  iap_banned: boolean;
  id_verified: boolean; // model government_id_approved
  id_verification_status?: EIdVerificationStatus | null; // only for models
  ig_url?: string;
  ig_followers?: number;
  ig_username?: string;
  is_customer?: boolean; // flag for me if I'm a paying customer and also for other users in chats for fans to see which should be marked as Paid
  is_password_set?: boolean; // flag for user to see if he has set a password (social login)
  is_subscribed_to?: boolean; // flag for user to see if he is subscribed to fm
  is_subscriber?: boolean; // flag for fm to see if user is subscribed to her
  is_broadcasting?: boolean; // flag for fm during sending a broadcast message (is ended with ws type broadcast_done)
  is_deleted?: boolean;
  fans_count?: number;
  image_count?: number;
  image_rate?: number;
  intro_enabled?: boolean;
  invisible: boolean;
  settings: {
    core: {
      warning_you_have_been_reported: boolean;
      allow_referrer_view_breakdown?: boolean;
    };
  };
  subscribers_count: number;
  subscription_enabled?: boolean;
  subscription_free_chats?: boolean;
  subscription_rate: number;
  subscriptions_earnings: number; // only for fms
  is_blocked?: boolean;
  is_blocked_by?: boolean;
  is_blocked_by_country?: boolean;
  is_followee?: boolean;
  is_follower?: boolean;
  is_online: boolean;
  is_staff: boolean;
  is_staff_mo?: boolean;
  is_vip: boolean;
  last_online: string;
  // media_blocked_for: boolean; // only for chat.other_user means you've blocked this user's media
  // media_blocked_from: boolean; // only for chat.other_user means other user has blocked you from sending media
  name: string;
  new_fm_onboarding?: boolean; // only present if model onboarded using the new join flow
  oauth_facebook_without_password?: boolean;
  oauth_twitter_without_password?: boolean;
  onboarding_v2?: boolean; // only present if user onboarded using new code verification flow
  os_version: string;
  payouts?: {
    email: string;
    method?: EPayoutMethod;
    last?: unknown; // only ever used for truthiness (`!user.payouts?.last`), never read into
  };
  paid_content_policy_confirmed?: boolean;
  payout_country_code?: string | null; // only needed for patch (alpha3)
  position_top?: number;
  push_enabled: boolean;
  private_feed: boolean;
  region_blacklist: boolean;
  requires_moderation?: boolean; // only present for model using new new_fm_onboarding that needs to be moderated
  show_review_banner: boolean;
  show_skinscore_banner: boolean;
  show_visibility_penalty_banner: boolean;
  show_subscription_free_chats?: boolean; // show toggle in set credit rate
  social_platform?: ESocialPlatform;
  social_platform_username?: string;
  social_platform_popularity?: ESocialPlatformPopularity;
  social_platform_status?: ESocialPlatformStatus | null; // only for models
  social_platform_status_reason?: string; // only for models
  subscription_commission: number; // only for fms
  tags: string[] | null;
  title: string;
  tier?: number | null;
  type: EUserType;
  twitter_connected?: boolean;
  two_factor_enabled?: boolean;
  udid: string;
  username_generated?: boolean; // only present if true
  video_count?: number;
  video_rates?: IVideoRate[];
  test_account?: boolean;
  has_favorites?: boolean;
  fans_30: number;
  subscriptions_30: number;
  sales_30: number;
  tips?: string[];
  deleted_scheduled?: boolean;
  delete_at?: string;
}

export interface IProviderConfig {
  is_guest?: boolean; // expired or no token
  app_keywords?: string; // competing apps
  global_keywords?: string; // all warning keywords used for fms
  payment_keywords?: string; // external payments
  show_promo_badges?: boolean;
  greeting_keywords?: string; // rude words in send to all
  features?: IProviderConfigFeatures;
  packages?: IProviderConfigPackage[];
  smart_logic?: boolean; // special logic for paypal ban protection
  usd_to_credit_ratio?: number;
  usdt_to_credit_ratio?: number;
  tips_min_amount?: number;
  tips_min_amount_with_message?: number;
  tips_max_amount?: number;
  tips_min_amount_credits?: number;
  tips_max_amount_credits?: number;
  tips_min_amount_with_message_credits?: number;
  tips_presets?: number[];
  tips_presets_credits?: number[];
  call_rate_options?: number[]; // allowed video call rates, credits per minute
  subscription_rate_options?: {
    value: number;
    label: string;
  }[];
  content_prices?: {
    usd: {
      step: number;
      min: {
        image: number;
        video: number;
      };
      max: {
        image: number;
        video: number;
      };
      default: {
        image: number;
        video: number;
      };
    };
    credits: {
      step: number;
      min: {
        image: number;
        video: number;
      };
      max: {
        image: number;
        video: number;
      };
      default: {
        image: number;
        video: number;
      };
    };
  };
  chat_notes_maximum?: number;
  allowed_self_destruct_hours?: number[];
  landers_v2?: boolean;
}

export enum EFeature {
  Broadcast = "broadcast",
  ExclusiveContentBanner = "exclusive_content_banner",
  NoDiscover = "no_discover",
  BankTransfer = "bank_transfer",
  PayPalTransfer = "paypal_transfer",
  PaxumTransfer = "paxum_transfer",
  CryptoTransfer = "crypto_transfer",
  Tips = "tips",
  UsdContent = "usd_content",
  CreditPackageSalesCommissions = "credit_package_sales_commissions",
  CryptoPayment = "crypto_payment",
  Vault = "vault",
  DiscoverCards = "discover_cards",
  TwoFactorAuthentication = "two_factor_authentication",
  PaidChatV2 = "paid_chat_v2",
  VideoCalls = "video_calls",
  EmojiReactions = "emoji_reactions",
}

interface IProviderConfigFeatures {
  [EFeature.Broadcast]?: boolean;
  [EFeature.ExclusiveContentBanner]?: boolean;
  [EFeature.NoDiscover]?: boolean;
  [EFeature.BankTransfer]?: boolean;
  [EFeature.PayPalTransfer]?: boolean;
  [EFeature.PaxumTransfer]?: boolean;
  [EFeature.CryptoTransfer]?: boolean;
  [EFeature.Tips]?: boolean;
  [EFeature.UsdContent]?: boolean;
  [EFeature.CreditPackageSalesCommissions]?: boolean;
  [EFeature.CryptoPayment]?: boolean;
  [EFeature.Vault]?: boolean;
  [EFeature.DiscoverCards]?: boolean;
  [EFeature.TwoFactorAuthentication]?: boolean;
  [EFeature.PaidChatV2]?: boolean;
  [EFeature.VideoCalls]?: boolean;
  [EFeature.EmojiReactions]?: boolean;
}

export interface IProviderConfigPackage {
  amount: number; // price
  credits: number;
  bonus?: number;
  bonus_credits?: number;
  default?: boolean; // selected by default
}

export enum ECreditCardBrand {
  VISA = "VISA",
  MASTER = "MASTER",
}

export interface ICreditCard {
  brand: ECreditCardBrand;
  id: string;
  last_4_numbers: string;
  // Only cards on a processor that can hold money pay for a call by card.
  can_pay_calls?: boolean;
}

export interface ISubscriptionInfo {
  user: IUserInfo;
  amount: number;
  status: ESubscriptionInfoStatus;
  next_billing: string; // date
  till: string; // date
  can_reactivate: boolean;
}

export enum ESubscriptionInfoStatus {
  ACTIVE = "active",
  CANCELED = "canceled",
}

export enum EAutoMessageType {
  Text = "text",
  Image = "image",
  Video = "video",
  Gif = "gif",
}

export interface IAutoMessageCategory {
  category: string;
  messages: IAutoMessage[];
  excerpt: string;
  sent_24h: number;
}

export interface IAutoMessage {
  _id?: string;
  type: EAutoMessageType;
  text?: string;
  gif_url?: string;
  width?: number;
  height?: number;
  media_id?: string; // bendinary
  public_id?: string; // cloudinary
  media?: IMediaType;
  price?: number;
  price_in_dollars?: boolean; // price in usd instead of Credits
  self_destruct?: boolean;
  self_destruct_hours?: number | null;
  __clientSideSendId?: string;
  __isUploadFailed?: boolean;
  __errorMessage?: string;
  __isBeingDeleted?: boolean;
}

export enum EAutoMessageCategory {
  User = "user",
  Follower = "follower",
  Subscriber = "subscriber",
  Fan = "fan",
}

export enum ECreditCardFormType {
  Default = "default",
  Compact = "compact",
}

export interface IMyPurchase {
  message_id: string;
  media: IMediaType;
  price: number;
  price_formatted: string;
  self_destruct: boolean;
  self_destruct_at: string | null;
  paid_at: string;
  sender: {
    _id: string;
    username: string;
    display_name: string;
    avatar: IMediaType;
  };
}

export interface IVaultMedia {
  _id: string;
  type: "image" | "video";
  public_id?: string; // item was uploaded to CLOUDINARY
  media_id?: string; // item was uploaded to BENDINARY
  width: number;
  height: number;
  video_length?: number;
  user_id: string;
  name: string;
  created_at: string; // string timestamp
  updated_at: string; // string timestamp
  rt: string; // src url
  st: string; // src url
  th: string; // src url
  video_url?: string; // src url
  is_current_chat: boolean;
  is_current_chat_paid: boolean;
}

export interface IIdVerificationInfo {
  media: null | {
    front?: {
      public_id: string;
      rt: string;
      st: string;
      th: string;
      type: "front";
      status: EIdVerificationMediaStatus;
      status_reason?: string;
    };
    back?: {
      public_id: string;
      rt: string;
      st: string;
      th: string;
      type: "back";
      status: EIdVerificationMediaStatus;
      status_reason?: string;
    };
    selfie?: {
      public_id: string;
      rt: string;
      st: string;
      th: string;
      type: "selfie";
      status: EIdVerificationMediaStatus;
      status_reason?: string;
    };
    video_selfie?: {
      public_id: string;
      rt: string;
      st: string;
      th: string;
      type: "video_selfie";
      status: EIdVerificationMediaStatus;
      status_reason?: string;
      video_url: string;
    };
  };
  status: EIdVerificationStatus;
}

export interface BlockedUser {
  _id: string;
  username: string;
  display_name: string;
  avatar?: IMediaType;
}

export interface BlockedCountry {
  code: string;
  name: string;
  blocked: boolean;
}
